import { AuthOIDCApi } from "@simbelapp/order-sdk";
import { useRawSDKApi } from "~/composables/api/useSDKApi";
import { useAuthStore } from "~/store/auth/auth.store";
import { useOidcClient } from "./useOidcClient";
import { getAuthEntryUrl } from "~/utils/helpers/auth/privacy";
import { parseDoceboLaunch } from "~/utils/helpers/auth/docebo";
import { isEmbeddedAuth } from "~/utils/helpers/auth/browser-context";
import { FetchCredentialsEnum, MicroserviceEnum } from "~/utils/enums/common.enums";
import { AUTH_REQUIRED_MESSAGE } from "~/utils/helpers/auth/oidc-contract";

let launch: Promise<string> | undefined;

/** Starts in the auth plugin, before profile restoration and mounting business screens. */
export function startDoceboLogin(): void {
  if (launch) return;
  const auth = useAuthStore();
  const entryUrl = getAuthEntryUrl();
  launch = (async () => {
    const input = parseDoceboLaunch(entryUrl, document.referrer, isEmbeddedAuth());
    const client = useOidcClient();
    const api = await useRawSDKApi(MicroserviceEnum.ORDERS, AuthOIDCApi, false);
    const session = await client.loginEmbedded(async ({ codeChallenge, nonce }) => {
      const result = await api.authorizeOidcDocebo(
        {
          origin: window.location.origin,
          authOidcDoceboAuthorizeDto: {
            clientId: input.clientId,
            authCode: input.authCode,
            launchOrigin: input.launchOrigin,
            oidcClientId: client.config.clientId,
            redirectUri: client.config.redirectUri,
            codeChallenge,
            nonce,
          },
        },
        { credentials: FetchCredentialsEnum.OMIT, signal: AbortSignal.timeout(15_000) },
      );
      input.authCode = "";
      return result.code;
    });
    auth.setOidcSession(session);
    await auth.login();
    if (!auth.isLoggedIn()) throw new Error(AUTH_REQUIRED_MESSAGE);
    return input.returnTo;
  })();
  // The entry page consumes the rejection; avoid an unhandled promise during mounting.
  void launch.catch(() => undefined);
}

export function consumeDoceboLogin(): Promise<string> {
  const pending = launch;
  launch = undefined;
  return pending ?? Promise.reject(new Error(AUTH_REQUIRED_MESSAGE));
}
